import { GridCustomCellProps } from '@progress/kendo-react-grid';
import moment, { Moment } from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../../../common/global/helpers';
import useBreakpoints from '../../../../../../common/hooks/useBreakpoints';
import useMount from '../../../../../../common/hooks/useMount';
import Grid from '../../../../../../common/widgets/Grid';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import {
  File,
  PresenceTypes,
  Timesheet,
  TimesheetBankHolidays,
} from '../../../../models/TimeAndAttendance';
import { resources } from '../../../../resources/resources';
import { timeAndAttendanceService } from '../../../../services/time-and-attendance';
import { ActionsCell, fileNameCell } from './CustomCells';
import { SubmitTimesheetDialog } from './SubmitTimesheetDialog';
import { TimeAndAttendanceActionButtons } from './TimeAndAttendanceActionButtons';
import { TimeAndAttendanceCalendar } from './TimeAndAttendanceCalendar';
import { TimeAndAttendaceCheckIn } from './TimeAndAttendanceCheckIn';
import { TimeAndAttendanceDialog } from './TimeAndAttendanceDialog';

export const TimeAndAttendance = () => {
  const { t: translate } = useTranslation();
  const [currentDate, setCurrentDate] = useState<Moment>(moment());
  const [showAttendanceDialog, setShowAttendanceDialog] = useState(false);
  const [showSubmitTimesheetDialog, setShowSubmitTimesheetDialog] =
    useState(false);
  const [timesheet, setTimesheet] = useState<Timesheet[]>();
  const [files, setFiles] = useState<File[]>();
  const [timesheetBankHolidays, setTimesheetBankHolidays] = useState<
    TimesheetBankHolidays[]
  >([]);
  const [selectedAttendance, setSelectedAttendance] = useState<Timesheet>();
  const [timesheetPresenceTypes, setTimesheetPresenceTypes] = useState<
    PresenceTypes[]
  >([]);

  const { isMobile } = useBreakpoints();
  useEffect(() => {
    const abortController = new AbortController();
    (async () => {
      setTimesheet(undefined);
      setFiles(undefined);

      const result = await timeAndAttendanceService.getTimesheet(currentDate, {
        signal: abortController?.signal,
      });

      if (abortController?.signal.aborted) {
        return;
      }

      if (!result.hasErrors) {
        setTimesheet(result.data);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }

      //get files
      const filesResult = await timeAndAttendanceService.getTimesheetFiles(
        currentDate,
        {
          signal: abortController?.signal,
        },
      );

      if (abortController?.signal.aborted) {
        return;
      }

      if (!filesResult.hasErrors) {
        setFiles(filesResult.data);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [currentDate, translate]);

  const loadTimeAndAttendancePresenceTypes = async (
    abortController?: AbortController,
  ) => {
    const result = await timeAndAttendanceService.getTimesheetBankHolidays({
      signal: abortController?.signal,
    });

    if (abortController?.signal.aborted) {
      return;
    }

    if (!result.hasErrors) {
      setTimesheetBankHolidays(result.data);
    } else {
      showErrorToast(translate(resources.errors.generic));
    }
  };

  const loadTimesheetBankHolidays = async (
    abortController?: AbortController,
  ) => {
    const r = await timeAndAttendanceService.getTimesheetPresenceTypes({
      signal: abortController?.signal,
    });

    if (abortController?.signal.aborted) {
      return;
    }

    if (!r.hasErrors) {
      setTimesheetPresenceTypes(r.data);
    } else {
      showErrorToast(translate(resources.errors.generic));
    }
  };

  useMount(abortController => {
    loadTimeAndAttendancePresenceTypes(abortController);
    loadTimesheetBankHolidays(abortController);
  });

  const handleDeleteFile = async (
    uploadId: number,
    abortController?: AbortController,
  ) => {
    const r = await timeAndAttendanceService.removeTimesheetPresenceFile(
      uploadId,
      {
        signal: abortController?.signal,
      },
    );

    if (abortController?.signal.aborted) {
      return;
    }

    if (!r.hasErrors) {
      showSuccessToast(
        translate(resources.timeAndAttendance.files.deleteSuccessfullyMessage),
      );

      setFiles(files?.filter(file => file.uploadId !== uploadId));
    } else {
      showErrorToast(translate(resources.errors.generic));
    }
  };

  const handleDownloadFile = async (
    uploadId: number,
    fileName: string,
    abortController?: AbortController,
  ) => {
    const resp = await timeAndAttendanceService.downloadTimesheetPresenceFile(
      uploadId,
      fileName,
      {
        signal: abortController?.signal,
      },
    );

    if (resp.hasErrors) {
      showErrorToast(translate(resources.errors.cannotGenerateFile));
    }
  };

  const ActionsCellCustomCell = (props: GridCustomCellProps) => (
    <ActionsCell
      {...props}
      handleDownloadFile={handleDownloadFile}
      handleDeleteFile={handleDeleteFile}
      isApproving={false}
    />
  );
  return (
    <div className="time-and-attendance-container">
      <div className="taa-action-buttons-container">
        <TimeAndAttendanceActionButtons
          isTimesheetLocked={timesheet?.some(tp => tp.submitted) ?? true}
          currentDate={currentDate}
          setCurrentDate={setCurrentDate}
          setShowAttendanceDialog={setShowAttendanceDialog}
          setShowSubmitTimesheetDialog={setShowSubmitTimesheetDialog}
          setSelectedAttendance={setSelectedAttendance}
        />
        <Loader loading={!timesheet} loader={<LoadingSpinner />}>
          <TimeAndAttendanceCalendar
            timesheetData={timesheet}
            isTimesheetBlocked={timesheet?.some(tp => tp.submitted) ?? true}
            timesheetBankHolidaysData={timesheetBankHolidays}
            setShowAttendanceDialog={setShowAttendanceDialog}
            currentDate={currentDate}
            setSelectedAttendance={setSelectedAttendance}
            bankHolidayPresenceType={timesheetPresenceTypes.find(
              pt =>
                pt.presenceTypeDescription.startsWith('Bank Holiday') ||
                pt.presenceTypeDescription.startsWith('Sarbatoare Legala'),
            )}
          />
        </Loader>
        {!isMobile && (
          <TimeAndAttendaceCheckIn
            isTimesheetBlocked={timesheet?.some(tp => tp.submitted) ?? true}
          />
        )}
        {files && files.length > 0 && (
          <>
            <h3>{translate(resources.timeAndAttendance.files.title)}</h3>
            <div className="my-account-grid acc-time-attendance-files-grid">
              <Grid
                data={files}
                sortable={false}
                columns={[
                  {
                    sortable: false,
                    cell: fileNameCell,
                  },
                  {
                    sortable: false,
                    cell: ActionsCellCustomCell,
                    headerClassName: 'grid-actions-cell',
                  },
                ]}
              />
            </div>
          </>
        )}

        {showAttendanceDialog && (
          <TimeAndAttendanceDialog
            currentDate={currentDate}
            setTimesheet={setTimesheet}
            setFiles={setFiles}
            setShowAttendanceDialog={setShowAttendanceDialog}
            selectedAttendance={selectedAttendance}
            setSelectedAttendance={setSelectedAttendance}
            availableTimesheetPresenceTypes={timesheetPresenceTypes}
          />
        )}
        {showSubmitTimesheetDialog && (
          <SubmitTimesheetDialog
            setTimesheet={setTimesheet}
            setShowSubmitTimesheetDialog={setShowSubmitTimesheetDialog}
            currentDate={currentDate}
          />
        )}
      </div>
    </div>
  );
};
